import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import {
  AlertTriangle,
  Archive,
  BarChart3,
  CircleCheck,
  Eye,
  HelpCircle,
  Layers,
  Package,
  PackageCheck,
  PieChart,
  RefreshCw,
  Repeat2,
  RotateCcw,
  ShieldAlert,
  Truck,
  UserCheck,
  Users,
  Warehouse,
  XCircle,
} from "lucide-react";
import AppContext from "../../../context/AppContext";
import translations from "../../common/Translations";
import CommonTooltip from "../../common/CommonTooltip";
import ActionIconButton from "../../common/ActionIconButton";
import ChartModal from "../user/static-charts/ChartModal";
import InventoryStackedBarCard, { shareText } from "./InventoryStackedBarCard";
import { Spinner } from "../../../Spinner";
import { toast } from "../../../utils/toast";
import { isSuperAdmin } from "../../../utils/permissions";
import { isRTL } from "../../../utils/rtl";
import { fetchInventoryDashboard } from "../../../service/MeterInventoryManagement";
import ReactSelector from "../../common/CommonReactSelector";
import {
  apiErrorMessage,
  itemTypeOptionsFor,
  useInventoryScope,
} from "../../InventoryManagement/inventoryFormShared";
import { normalizeStoreStatus } from "../../InventoryManagement/inventoryStoreAvailability";

// Same order, icons and badge colours as the status badges on the inventory list.
// `chart` is the slice/segment colour: the badge text colours repeat (Removed and
// Damaged are both red, Lost and Retired both slate), which is fine on a labelled
// badge but makes two slices of the donut indistinguishable, so every status gets
// its own hue here.
const STATUSES = [
  { code: "AVAILABLE", label: "inventoryStatusAvailable", tip: "inventoryStatusTipAvailable", icon: PackageCheck, bg: "#DCF5E3", text: "#1E9E57", chart: "#1E9E57" },
  { code: "ASSIGNED", label: "inventoryStatusAssigned", tip: "inventoryStatusTipAssigned", icon: UserCheck, bg: "#DEE8FF", text: "#2563EB", chart: "#2563EB" },
  { code: "REASSIGNED", label: "inventoryStatusReassigned", tip: "inventoryStatusTipReassigned", icon: Repeat2, bg: "#CCFBF1", text: "#0F766E", chart: "#14B8A6" },
  { code: "IN_TRANSIT", label: "inventoryStatusInTransit", tip: "inventoryStatusTipInTransit", icon: Truck, bg: "#FFF1D6", text: "#F2930D", chart: "#F2930D" },
  { code: "INSTALLED", label: "inventoryStatusInstalled", tip: "inventoryStatusTipInstalled", icon: CircleCheck, bg: "#E0E7FF", text: "#4338CA", chart: "#6366F1" },
  { code: "REMOVED", label: "inventoryStatusRemoved", tip: "inventoryStatusTipRemoved", icon: XCircle, bg: "#FEE2E2", text: "#B91C1C", chart: "#EF4444" },
  { code: "RETURNED", label: "inventoryStatusReturned", tip: "inventoryStatusTipReturned", icon: RotateCcw, bg: "#EDE9FE", text: "#6D28D9", chart: "#8B5CF6" },
  { code: "DAMAGED", label: "inventoryStatusDamaged", tip: "inventoryStatusTipDamaged", icon: AlertTriangle, bg: "#FEE2E2", text: "#B91C1C", chart: "#BE185D" },
  { code: "QUARANTINED", label: "inventoryStatusQuarantined", tip: "inventoryStatusTipQuarantined", icon: ShieldAlert, bg: "#FEF3C7", text: "#B45309", chart: "#CA8A04" },
  { code: "LOST", label: "inventoryStatusLost", tip: "inventoryStatusTipLost", icon: HelpCircle, bg: "#E2E8F0", text: "#475569", chart: "#475569" },
  { code: "RETIRED", label: "inventoryStatusRetired", tip: "inventoryStatusTipRetired", icon: Archive, bg: "#E2E8F0", text: "#475569", chart: "#A8A29E" },
];
const STATUS_BY_CODE = Object.fromEntries(STATUSES.map((status) => [status.code, status]));

// The statuses a group's items are broken down into (Assigned includes Reassigned),
// plus OTHER for whatever the group holds beyond them (removed, returned, ...), so
// each group's bar adds up to its total.
const GROUP_SERIES = [
  { key: "ASSIGNED", label: "inventoryStatusAssigned", icon: UserCheck, color: STATUS_BY_CODE.ASSIGNED.chart },
  { key: "IN_TRANSIT", label: "inventoryStatusInTransit", icon: Truck, color: STATUS_BY_CODE.IN_TRANSIT.chart },
  { key: "INSTALLED", label: "inventoryStatusInstalled", icon: CircleCheck, color: STATUS_BY_CODE.INSTALLED.chart },
  { key: "OTHER", label: "inventoryDashboardOther", icon: Layers, color: "#94A3B8" },
];

const TOOLTIP_CLASS = "w-max max-w-[min(15rem,calc(100vw-1.5rem))] [text-wrap:balance]";

const toCount = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : 0;
};

const toCoordinate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const statusKey = (value) => String(value ?? "").trim().toUpperCase().replace(/[\s-]+/g, "_");

// Status counts may arrive as [{ status, count }] or as a { STATUS: count } map;
// either way they come back as a { STATUS: count } map.
const toStatusCounts = (value) => {
  if (Array.isArray(value)) {
    return value.reduce((counts, row) => {
      const code = statusKey(row?.status ?? row?.statusCode ?? row?.name);
      if (code) counts[code] = (counts[code] || 0) + toCount(row?.count ?? row?.total ?? row?.value);
      return counts;
    }, {});
  }
  if (value && typeof value === "object") {
    return Object.entries(value).reduce((counts, [key, count]) => {
      counts[statusKey(key)] = toCount(count);
      return counts;
    }, {});
  }
  return {};
};

const sumCounts = (counts) => Object.values(counts).reduce((sum, count) => sum + count, 0);

// GET /api/v1/inventory/dashboard (body.value):
// { totalItems, statusCounts: { STATUS: n },
//   groups: [{ groupId, groupName, totalItems, assigned, inTransit, installed, statusCounts }],
//   stores: [{ storeId, storeName, location, latitude, longitude, active, totalItems,
//              statusCounts, incomingInTransit, outgoingInTransit }],
//   itemsWithoutStore }
// A group's `assigned` already includes its REASSIGNED items, so the flat fields are
// used for the breakdown and statusCounts only as a fallback. `other` is whatever of
// the group's total falls outside the three.
// `stores` stays null when the backend doesn't send it, so the store card is left
// out rather than claiming there are no stores.
export const normalizeDashboard = (payload) => {
  const statusCounts = toStatusCounts(payload?.statusCounts);
  const total = toCount(payload?.totalItems) || sumCounts(statusCounts);
  const groups = (Array.isArray(payload?.groups) ? payload.groups : [])
    .map((group, index) => {
      const groupStatusCounts = toStatusCounts(group?.statusCounts);
      const counts = {
        ASSIGNED: toCount(
          group?.assigned ?? (groupStatusCounts.ASSIGNED || 0) + (groupStatusCounts.REASSIGNED || 0)
        ),
        IN_TRANSIT: toCount(group?.inTransit ?? groupStatusCounts.IN_TRANSIT),
        INSTALLED: toCount(group?.installed ?? groupStatusCounts.INSTALLED),
      };
      const groupTotal = toCount(group?.totalItems) || sumCounts(groupStatusCounts) || sumCounts(counts);
      return {
        id: group?.groupId ?? `group-${index}`,
        name: group?.groupName ?? "",
        counts,
        other: Math.max(groupTotal - sumCounts(counts), 0),
        total: groupTotal,
      };
    })
    .sort((a, b) => b.total - a.total);
  const stores = Array.isArray(payload?.stores)
    ? payload.stores
        .map((store, index) => {
          const storeStatusCounts = toStatusCounts(store?.statusCounts);
          return {
            id: store?.storeId ?? store?.id ?? `store-${index}`,
            name: store?.storeName ?? store?.name ?? "",
            location: store?.location ?? store?.storeLocation ?? store?.address ?? "",
            latitude: toCoordinate(store?.latitude),
            longitude: toCoordinate(store?.longitude),
            active: normalizeStoreStatus({ ...store, isActive: store?.isActive ?? store?.active }) === "active",
            statusCounts: storeStatusCounts,
            total: toCount(store?.totalItems) || sumCounts(storeStatusCounts),
            incomingInTransit: toCount(store?.incomingInTransit),
            outgoingInTransit: toCount(store?.outgoingInTransit),
          };
        })
        .sort((a, b) => b.total - a.total)
    : null;
  return { total, statusCounts, groups, stores, itemsWithoutStore: toCount(payload?.itemsWithoutStore) };
};

const EMPTY_DASHBOARD = { total: 0, statusCounts: {}, groups: [], stores: null, itemsWithoutStore: 0 };

const formatNumber = (value) => Number(value || 0).toLocaleString();

/**
 * Inventory tab of Dashboard Management, laid out like the User dashboard: a row
 * of headline counts, then chart cards. "Items by status" is a donut (or bar) of
 * every status with its count and share beside it; "Items assigned to groups" and
 * "Items by store" are stacked bars per group / store (InventoryStackedBarCard).
 * Organization and project come from the header selectors, like the inventory
 * list; the item type is picked here. Any change reloads the counts.
 */
const InventoryDashboard = () => {
  const { theme, langMode } = useContext(AppContext);
  const t = translations[langMode] || translations.en;
  const isDark = theme === "dark" || theme === "high-contrast";
  const isHighContrast = theme === "high-contrast";
  const isRtl = isRTL(langMode);
  const { organizationId, projectId } = useInventoryScope();
  const superAdmin = isSuperAdmin();
  const scopedOrganizationId = superAdmin ? organizationId : undefined;
  // The API rejects a super admin's call without an organization, so it isn't made.
  const missingOrganization = superAdmin && !organizationId;

  const [itemType, setItemType] = useState(null);
  const [data, setData] = useState(EMPTY_DASHBOARD);
  const [loading, setLoading] = useState(false);
  const [statusView, setStatusView] = useState("donut");
  const [showStatusViewModal, setShowStatusViewModal] = useState(false);
  const itemTypeOptions = useMemo(() => itemTypeOptionsFor(t), [t]);

  const load = useCallback(async () => {
    if (missingOrganization) {
      setData(EMPTY_DASHBOARD);
      return;
    }
    setLoading(true);
    try {
      const payload = await fetchInventoryDashboard({
        organizationId: scopedOrganizationId,
        projectId,
        itemType: itemType?.value,
      });
      setData(normalizeDashboard(payload));
    } catch (error) {
      setData(EMPTY_DASHBOARD);
      toast.error(apiErrorMessage(error, t.inventoryDashboardLoadFailed));
    } finally {
      setLoading(false);
    }
  }, [missingOrganization, scopedOrganizationId, projectId, itemType, t]);

  useEffect(() => {
    load();
  }, [load]);

  // Every known status is shown, even at zero; unknown ones from the backend
  // (top level or per store) are appended.
  const statusRows = useMemo(() => {
    const unknownCodes = new Set(
      [data.statusCounts, ...(data.stores || []).map((store) => store.statusCounts)]
        .flatMap((counts) => Object.keys(counts))
        .filter((code) => !STATUS_BY_CODE[code])
    );
    const extra = [...unknownCodes].map((code) => ({ code, labelText: code, icon: Package, bg: "#E2E8F0", text: "#475569", chart: "#64748B" }));
    return [...STATUSES, ...extra].map((status) => ({
      ...status,
      name: status.labelText ?? t[status.label] ?? status.code,
      count: data.statusCounts[status.code] || 0,
    }));
  }, [data.statusCounts, data.stores, t]);

  // Only populated statuses become slices; the list beside the chart still shows all.
  const plottedStatusRows = useMemo(() => statusRows.filter((status) => status.count > 0), [statusRows]);
  // Share of the whole inventory is measured against the API total, which can
  // include statuses the counts leave out, so fall back to the summed counts.
  const statusTotal = data.total || sumCounts(data.statusCounts);

  // ---- groups ---------------------------------------------------------------
  const groupRows = useMemo(
    () => data.groups.map((group) => ({ id: group.id, name: group.name, total: group.total, values: { ...group.counts, OTHER: group.other } })),
    [data.groups]
  );
  const groupSeries = useMemo(() => GROUP_SERIES.map((series) => ({ ...series, label: t[series.label] })), [t]);
  const groupTotals = useMemo(() => {
    const sum = (key) => groupRows.reduce((acc, row) => acc + (row.values[key] || 0), 0);
    return { total: groupRows.reduce((acc, row) => acc + row.total, 0), ASSIGNED: sum("ASSIGNED"), IN_TRANSIT: sum("IN_TRANSIT"), INSTALLED: sum("INSTALLED") };
  }, [groupRows]);
  const groupTiles = [
    { key: "groups", text: t.inventoryDashboardGroups, value: groupRows.length, Icon: Users },
    ...GROUP_SERIES.filter((series) => series.key !== "OTHER").map((series) => ({
      key: series.key,
      text: t[series.label],
      value: groupTotals[series.key],
      Icon: series.icon,
      color: series.color,
      share: shareText(groupTotals[series.key], groupTotals.total),
    })),
  ];

  // ---- stores ---------------------------------------------------------------
  const storeRows = useMemo(
    () =>
      (data.stores || []).map((store) => {
        const transit = [
          store.incomingInTransit > 0 && `${t.inventoryDashboardIncoming}: ${formatNumber(store.incomingInTransit)}`,
          store.outgoingInTransit > 0 && `${t.inventoryDashboardOutgoing}: ${formatNumber(store.outgoingInTransit)}`,
        ];
        return {
          id: store.id,
          name: store.active ? store.name : `${store.name} (${t.inventoryDashboardStoreInactive})`,
          total: store.total,
          values: store.statusCounts,
          detail: store.location,
          tooltipLines: [store.location, ...transit],
        };
      }),
    [data.stores, t]
  );
  const storeSeries = useMemo(
    () => statusRows.map((status) => ({ key: status.code, label: status.name, icon: status.icon, color: status.chart })),
    [statusRows]
  );
  const storeTiles = useMemo(() => {
    const stores = data.stores || [];
    const itemsInStores = stores.reduce((acc, store) => acc + store.total, 0);
    const sum = (code) => stores.reduce((acc, store) => acc + (store.statusCounts[code] || 0), 0);
    const attention = sum("DAMAGED") + sum("QUARANTINED") + sum("LOST");
    return [
      { key: "stores", text: t.inventoryDashboardStores, value: stores.length, Icon: Warehouse },
      { key: "items", text: t.inventoryDashboardItemsInStores, value: itemsInStores, Icon: Package },
      {
        key: "available",
        text: t.inventoryStatusAvailable,
        value: sum("AVAILABLE"),
        Icon: PackageCheck,
        color: STATUS_BY_CODE.AVAILABLE.chart,
        share: shareText(sum("AVAILABLE"), itemsInStores),
      },
      {
        key: "attention",
        text: t.inventoryDashboardNeedsAttention,
        value: attention,
        Icon: AlertTriangle,
        color: STATUS_BY_CODE.DAMAGED.chart,
        share: shareText(attention, itemsInStores),
      },
    ];
  }, [data.stores, t]);

  // ---- theme ----------------------------------------------------------------
  const inkPrimary = isHighContrast ? "#FDE047" : isDark ? "#E5E7EB" : "#334155";
  const inkMuted = isHighContrast ? "#FDE047" : isDark ? "#9CA3AF" : "#64748B";
  const gridLine = isDark ? "#374151" : "#E2E8F0";
  const surface = isHighContrast ? "#000000" : isDark ? "#1F2937" : "#FFFFFF";

  const chartCardClass = `rounded-2xl border shadow-md hover:shadow-lg transition-shadow duration-300 ease-in-out p-3 sm:p-4 w-full min-w-0 flex flex-col ${
    theme === "dark"
      ? "bg-gray-800 border-gray-700 text-gray-200"
      : isHighContrast
        ? "bg-black border-2 border-yellow-500 text-yellow-300"
        : "bg-white border-gray-200/70 text-gray-800"
  }`;
  const mutedText = isHighContrast ? "text-yellow-300" : theme === "dark" ? "text-gray-400" : "text-gray-500";
  const subtleBorder = theme === "dark" ? "border-gray-700" : isHighContrast ? "border-yellow-500/40" : "border-slate-100";

  // ---- headline cards (same build as the User dashboard's count cards) --------
  const kpiCards = [
    { key: "total", title: t.inventoryDashboardTotal, value: statusTotal, icon: Package, border: "border-[#dbe7f4]", glow: "bg-blue-100/50", box: "border-blue-200 bg-blue-100/80", ink: "text-blue-600" },
    { key: "available", title: t.inventoryStatusAvailable, value: data.statusCounts.AVAILABLE || 0, icon: PackageCheck, border: "border-[#d6efe3]", glow: "bg-green-100/60", box: "border-green-200 bg-green-100/80", ink: "text-green-600" },
    { key: "assigned", title: t.inventoryStatusAssigned, value: data.statusCounts.ASSIGNED || 0, icon: UserCheck, border: "border-[#e6defa]", glow: "bg-purple-100/60", box: "border-purple-200 bg-purple-100/80", ink: "text-purple-500" },
    { key: "installed", title: t.inventoryStatusInstalled, value: data.statusCounts.INSTALLED || 0, icon: CircleCheck, border: "border-[#e0e7ff]", glow: "bg-indigo-100/60", box: "border-indigo-200 bg-indigo-100/80", ink: "text-indigo-600" },
  ];
  const kpiThemeClass =
    theme === "dark" ? "bg-gray-800 text-gray-200" : isHighContrast ? "bg-black text-yellow-300 border-2 border-yellow-500" : "bg-gray-50 text-gray-800";
  const kpiTitleClass = theme === "dark" ? "text-gray-400" : isHighContrast ? "text-yellow-300" : "text-gray-600";
  const kpiValueClass = theme === "dark" ? "text-gray-100" : isHighContrast ? "text-yellow-300" : "text-gray-900";

  // ---- Items by status ------------------------------------------------------
  const donutOptions = useMemo(
    () => ({
      chart: { type: "donut", fontFamily: "inherit", background: "transparent", toolbar: { show: false }, foreColor: inkPrimary },
      labels: plottedStatusRows.map((status) => status.name),
      colors: plottedStatusRows.map((status) => status.chart),
      legend: { show: false },
      stroke: { show: true, width: 2, colors: [surface] },
      dataLabels: {
        enabled: true,
        // Labels on thin slices only collide; their numbers are in the list beside.
        formatter: (value) => (value >= 4 ? `${Math.round(value)}%` : ""),
        style: { fontSize: "11px", fontWeight: 600 },
        dropShadow: { enabled: false },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "64%",
            labels: {
              show: true,
              name: { show: true, fontSize: "13px", color: inkMuted, offsetY: -4 },
              value: {
                show: true,
                fontSize: "20px",
                fontWeight: 700,
                color: inkPrimary,
                offsetY: 4,
                formatter: (value) => formatNumber(value),
              },
              total: {
                show: true,
                showAlways: true,
                label: t.inventoryDashboardTotal,
                fontSize: "13px",
                color: inkMuted,
                formatter: () => formatNumber(statusTotal),
              },
            },
          },
        },
      },
      tooltip: {
        theme: isDark ? "dark" : "light",
        y: { formatter: (value) => `${formatNumber(value)} ${t.inventoryDashboardItems} (${shareText(value, statusTotal)})` },
      },
      states: { active: { filter: { type: "none" } } },
    }),
    [plottedStatusRows, statusTotal, inkPrimary, inkMuted, surface, isDark, t]
  );

  const statusBarOptions = useMemo(
    () => ({
      chart: { type: "bar", fontFamily: "inherit", background: "transparent", toolbar: { show: false }, animations: { enabled: false } },
      colors: statusRows.map((status) => status.chart),
      plotOptions: {
        bar: { horizontal: true, distributed: true, barHeight: "62%", borderRadius: 4, borderRadiusApplication: "end", dataLabels: { position: "top" } },
      },
      dataLabels: {
        enabled: true,
        offsetX: isRtl ? -22 : 22,
        formatter: (value) => formatNumber(value),
        style: { fontSize: "11px", fontWeight: 600, colors: [inkPrimary] },
      },
      legend: { show: false },
      xaxis: {
        categories: statusRows.map((status) => status.name),
        labels: { style: { colors: inkMuted, fontSize: "11px" }, formatter: (value) => formatNumber(Math.round(toCount(value))) },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { reversed: isRtl, labels: { maxWidth: 120, style: { colors: inkPrimary, fontSize: "11px" } } },
      grid: {
        borderColor: gridLine,
        strokeDashArray: 3,
        xaxis: { lines: { show: true } },
        yaxis: { lines: { show: false } },
        padding: { left: isRtl ? 40 : 4, right: isRtl ? 4 : 40, top: 0, bottom: 0 },
      },
      tooltip: {
        theme: isDark ? "dark" : "light",
        y: {
          title: { formatter: () => "" },
          formatter: (value) => `${formatNumber(value)} ${t.inventoryDashboardItems} (${shareText(value, statusTotal)})`,
        },
      },
      states: { active: { filter: { type: "none" } } },
    }),
    [statusRows, statusTotal, inkPrimary, inkMuted, gridLine, isDark, isRtl, t]
  );

  const statusViewOptions = [
    {
      key: "donut",
      value: "donut",
      name: t.Piechart,
      description: t.inventoryDashboardDonutHint,
      icon: <PieChart className="h-9 w-9 text-yellow-500 transition-colors duration-200" />,
    },
    {
      key: "bar",
      value: "bar",
      name: t.barChartView,
      description: t.inventoryDashboardStatusBarHint,
      icon: <BarChart3 className="h-9 w-9 text-blue-600 transition-colors duration-200" />,
    },
  ];

  const emptyState = (Icon, message) => (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 px-4 py-10 text-center">
      <span
        className={`flex h-12 w-12 items-center justify-center rounded-full ${
          isHighContrast ? "bg-black text-yellow-300" : theme === "dark" ? "bg-[#236DB4]/20 text-[#236DB4]" : "bg-[#236DB4]/10 text-[#236DB4]"
        }`}
      >
        <Icon className="h-6 w-6" />
      </span>
      <p className={`text-sm ${mutedText}`}>{message}</p>
    </div>
  );

  // ---- render ---------------------------------------------------------------
  const statusCard = (
    <section className={chartCardClass} data-testid="inventory-dashboard-status-card">
      <div className="mb-3 flex w-full items-start justify-between gap-2 px-1">
        <h3 className="flex min-w-0 flex-1 items-center gap-1.5 sm:gap-2 text-xs sm:text-base font-semibold">
          <PieChart className="shrink-0 text-purple-500" size={18} />
          <span className="truncate">{t.inventoryDashboardStatusTitle}</span>
        </h3>
        <ActionIconButton
          tone="gray"
          className="shrink-0 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
          onClick={() => setShowStatusViewModal(true)}
          ariaLabel={t.selectView}
          tooltipText={t.selectView}
          tooltipPosition="top"
        >
          <Eye size={18} />
        </ActionIconButton>
      </div>
      {statusTotal === 0 ? (
        emptyState(Package, t.inventoryDashboardNoItems)
      ) : (
        <div className="flex flex-1 flex-col gap-4 lg:flex-row lg:items-center">
          <div className="h-[300px] w-full min-w-0 sm:h-[340px] lg:w-1/2">
            {statusView === "bar" ? (
              <ReactApexChart
                key={`status-bar-${isRtl ? "rtl" : "ltr"}`}
                options={statusBarOptions}
                series={[{ name: t.inventoryDashboardStatusTitle, data: statusRows.map((status) => status.count) }]}
                type="bar"
                width="100%"
                height="100%"
              />
            ) : (
              <ReactApexChart
                key={`status-donut-${plottedStatusRows.map((status) => status.code).join("|")}`}
                options={donutOptions}
                series={plottedStatusRows.map((status) => status.count)}
                type="donut"
                width="100%"
                height="100%"
              />
            )}
          </div>

          {/* Every status with its colour, count and share -- the chart's full legend. */}
          <div className="grid w-full min-w-0 grid-cols-1 gap-2 min-[420px]:grid-cols-2 lg:w-1/2">
            {statusRows.map((status) => {
              const Icon = status.icon;
              return (
                <CommonTooltip
                  key={status.code}
                  text={status.tip ? t[status.tip] : status.name}
                  position="top"
                  disableDefaultDescription
                  tooltipClassName={TOOLTIP_CLASS}
                  fitContent
                  clampToViewport
                  className="w-full cursor-default"
                >
                  <div
                    className={`flex w-full items-center gap-2.5 rounded-xl border px-2.5 py-2 ${subtleBorder} ${
                      status.count === 0 ? "opacity-60" : ""
                    }`}
                    data-testid={`inventory-dashboard-status-${status.code}`}
                  >
                    <span className="h-8 w-1 shrink-0 rounded-full" style={{ backgroundColor: status.chart }} />
                    <span
                      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                      style={{ backgroundColor: status.bg, color: status.text }}
                    >
                      <Icon className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className={`truncate text-xs ${mutedText}`}>{status.name}</p>
                      <p className="text-base font-semibold leading-tight tabular-nums">{formatNumber(status.count)}</p>
                    </div>
                    <span className={`shrink-0 text-xs font-medium tabular-nums ${mutedText}`}>
                      {shareText(status.count, statusTotal)}
                    </span>
                  </div>
                </CommonTooltip>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );

  return (
    <div className="flex w-full min-w-0 flex-col gap-4 sm:gap-6 pb-6 sm:pb-8 text-start" data-testid="inventory-dashboard">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-base sm:text-lg font-semibold">{t.inventoryDashboard}</h2>
          <p className={`mt-0.5 text-xs sm:text-sm ${mutedText}`}>{t.inventoryDashboardSubtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-full sm:w-56">
            <ReactSelector
              value={itemType}
              options={itemTypeOptions}
              onChange={(option) => setItemType(option || null)}
              placeholder={t.inventoryDashboardAllItemTypes}
              isClearable
              height="36px"
              menuPortalTarget={document.body}
              dataTestId="inventory-dashboard-item-type"
            />
          </div>
          <CommonTooltip
            text={t.refresh}
            position="top"
            disableDefaultDescription
            tooltipClassName={TOOLTIP_CLASS}
            fitContent
            clampToViewport
          >
            <button
              type="button"
              onClick={load}
              disabled={loading}
              aria-label={t.refresh}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-gray-700 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
              data-testid="inventory-dashboard-refresh"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          </CommonTooltip>
        </div>
      </div>

      {missingOrganization ? (
        <div className={chartCardClass}>{emptyState(Package, t.inventoryDashboardSelectOrganization)}</div>
      ) : loading && !data.total && !data.groups.length ? (
        <Spinner />
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5 sm:gap-5">
            {kpiCards.map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.key}
                  className={`relative flex w-full flex-row items-center justify-between overflow-hidden rounded-2xl border p-3.5 shadow-md transition-all duration-300 ease-in-out hover:scale-[1.02] hover:shadow-2xl sm:p-5 sm:shadow-xl ${kpiThemeClass} ${card.border}`}
                  data-testid={`inventory-dashboard-kpi-${card.key}`}
                >
                  <div className={`pointer-events-none absolute ${isRtl ? "-left-6" : "-right-6"} -top-6 h-20 w-20 rounded-full blur-xl ${card.glow}`} />
                  <div className="flex min-w-0 flex-col items-start gap-1">
                    <h3 className={`truncate text-xs sm:text-sm font-medium uppercase tracking-[0.03em] ${kpiTitleClass}`}>{card.title}</h3>
                    <p
                      className={`text-xl sm:text-2xl font-medium leading-none tabular-nums ${kpiValueClass}`}
                      data-testid={card.key === "total" ? "inventory-dashboard-total" : undefined}
                    >
                      {formatNumber(card.value)}
                    </p>
                  </div>
                  <div className={`${isRtl ? "mr-3 sm:mr-4" : "ml-3 sm:ml-4"} flex h-11 w-11 sm:h-14 sm:w-14 shrink-0 items-center justify-center rounded-xl sm:rounded-2xl border shadow-sm ${card.box}`}>
                    <Icon className={`h-6 w-6 sm:h-7 sm:w-7 ${card.ink}`} />
                  </div>
                </div>
              );
            })}
          </div>

          {statusCard}

          {/* Left out until the backend sends `stores`. */}
          {data.stores && (
            <InventoryStackedBarCard
              title={t.inventoryDashboardStoreTitle}
              subtitle={t.inventoryDashboardStoreSubtitle}
              Icon={Warehouse}
              iconClass="text-emerald-600"
              rows={storeRows}
              series={storeSeries}
              tiles={storeTiles}
              nameHeader={t.inventoryColStore}
              totalHeader={t.inventoryDashboardTotal}
              detailHeader={t.inventoryStoreLocation}
              emptyIcon={Warehouse}
              emptyMessage={t.inventoryDashboardNoStores}
              barHint={t.inventoryDashboardStoreBarHint}
              tableHint={t.inventoryDashboardStoreTableHint}
              showAllText={t.inventoryDashboardShowAllStores}
              showTopText={t.inventoryDashboardShowTopStores}
              footnote={
                data.itemsWithoutStore > 0
                  ? t.inventoryDashboardItemsWithoutStore.replace("{count}", formatNumber(data.itemsWithoutStore))
                  : null
              }
              testId="inventory-dashboard-store-card"
            />
          )}

          <InventoryStackedBarCard
            title={t.inventoryDashboardGroupTitle}
            subtitle={t.inventoryDashboardGroupSubtitle}
            Icon={Users}
            rows={groupRows}
            series={groupSeries}
            tiles={groupTiles}
            nameHeader={t.inventoryDashboardGroup}
            totalHeader={t.inventoryDashboardGroupTotal}
            emptyIcon={Users}
            emptyMessage={t.inventoryDashboardNoGroups}
            barHint={t.inventoryDashboardGroupBarHint}
            tableHint={t.inventoryDashboardTableHint}
            showAllText={t.inventoryDashboardShowAllGroups}
            showTopText={t.inventoryDashboardShowTopGroups}
            testId="inventory-dashboard-group-card"
          />
        </>
      )}

      <ChartModal
        showModal={showStatusViewModal}
        setShowModal={setShowStatusViewModal}
        options={statusViewOptions}
        value={statusView}
        onSelect={setStatusView}
        title={t.selectView}
      />
    </div>
  );
};

export default InventoryDashboard;
